import React from 'react';
import { Badge } from '@components/atoms/Badge';
import { Button } from '@components/atoms/Button';
import { Card } from '@components/atoms/Card';
import {
  AttachMoney,
  CalendarMonth,
  CheckCircle,
  Cancel,
  Reply,
} from '@mui/icons-material';
import { BudgetProposal } from '@models/budget.model';
import { format } from 'date-fns';
import { parseTaskDate } from '@utils/dateOnlyParser';
import { formatCurrency } from './utils';
import { SmallDocumentList } from '@components/molecules/SmallDocumentList';

interface ProposalCardProps {
  proposal: BudgetProposal;
  organizationId: string | undefined;
  budgetRequestStatus: string;
  onAccept: (proposalId: string) => void;
  onReject: (proposalId: string) => void;
  onCounter: (proposal: BudgetProposal) => void;
  showDocuments?: boolean;
}

export const ProposalCard: React.FC<ProposalCardProps> = ({
  proposal,
  organizationId,
  budgetRequestStatus,
  onAccept,
  onReject,
  onCounter,
  showDocuments = false,
}) => {
  const isCounterProposal = proposal.isCounterProposal;
  const borderColor = isCounterProposal
    ? 'border-blue-200'
    : 'border-amber-200';
  const bgColor = isCounterProposal
    ? 'bg-blue-50 dark:bg-blue-900/20'
    : 'bg-amber-50 dark:bg-amber-900/20';

  return (
    <div className="relative pl-12">
      {/* Timeline dot */}
      <div
        className={`absolute left-3 top-3 w-4 h-4 rounded-full ${
          isCounterProposal ? 'bg-blue-500' : 'bg-amber-500'
        } border-3 border-white shadow-sm z-10 flex items-center justify-center`}>
        {isCounterProposal && <Reply className="text-white text-xs" />}
      </div>

      <Card className={`p-4 ${borderColor} ${bgColor} border`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h5 className="font-semibold text-sm text-gray-900 dark:text-white">
              {proposal.proposingOrganization?.name}
            </h5>
            <Badge
              size="sm"
              variant={
                proposal.status === 'ACCEPTED'
                  ? 'success'
                  : proposal.status === 'REJECTED'
                    ? 'error'
                    : proposal.status === 'SUBMITTED'
                      ? 'warning'
                      : 'default'
              }>
              {proposal.status}
            </Badge>
            {proposal.isCounterProposal && (
              <Badge size="sm" variant="primary">
                <Reply className="w-3 h-3 mr-1" />
                Counter
              </Badge>
            )}
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {format(new Date(proposal.createdAt), 'MMM dd HH:mm')}
          </span>
        </div>

        {/* Basic proposal info */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2">
            <AttachMoney className="w-4 h-4 text-gray-500" />
            <span className="font-semibold text-gray-900 dark:text-white">
              {formatCurrency(proposal.proposedAmount)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CalendarMonth className="w-4 h-4 text-gray-500" />
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {format(
                parseTaskDate(proposal.proposedStartDate) || new Date(),
                'MMM dd',
              )}{' '}
              -{' '}
              {format(
                parseTaskDate(proposal.proposedEndDate) || new Date(),
                'MMM dd',
              )}
            </span>
          </div>
        </div>

        {proposal.description && (
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
            {proposal.description}
          </p>
        )}

        {/* Documents Section - Only show if showDocuments prop is true */}
        {showDocuments && proposal.documents && proposal.documents.length > 0 && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Attached Documents
            </p>
            <SmallDocumentList
              documents={proposal.documents}
              showDelete={false}
            />
          </div>
        )}

        {/* Action buttons for proposals */}
        {proposal.status === 'SUBMITTED' &&
          budgetRequestStatus === 'ACTIVE' &&
          proposal.proposingOrganizationId !== organizationId && (
            <div className="flex gap-2 flex-wrap">
              <Button
                size="md"
                variant="primary"
                leftIcon={<CheckCircle />}
                onClick={() => onAccept(proposal.budgetProposalId)}>
                Accept
              </Button>
              <Button
                size="md"
                variant="tertiary"
                leftIcon={<Reply />}
                onClick={() => onCounter(proposal)}>
                Counter
              </Button>
              <Button
                size="md"
                variant="tertiary"
                leftIcon={<Cancel />}
                onClick={() => onReject(proposal.budgetProposalId)}>
                Reject
              </Button>
            </div>
          )}
      </Card>
    </div>
  );
};
